import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { sortBy } from 'lodash';

import Box from '@mui/material/Box';

import {
  getFragmentData,
  GroupingModeEnum,
  TotalModeEnum,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import { invariant } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { useContextualizedFields } from '../../hooks/useContextualizedFields.hook';
import { SINGLETON_GROUP_LABEL } from '../../components/ServiceUsages/GroupBanner';
import { isInManualGroupingMode } from '../../components/ServiceUsages/Groups';
import { ServiceUsagesGlobalTotal } from '../../components/ServiceUsages/GlobalTotal';
import type { ServiceUsagesGlobalTotalOptions } from '../../components/ServiceUsages/GlobalTotal';
import type { GlobalTotalOptionsGroup } from '../../components/ServiceUsages/utils/global-total-option-items.util';
import {
  getTotalCalculationErrorMessages,
  shouldShowGlobalTotal,
} from '../../components/ServiceUsages/TotalCard';
import { buildGroupTotalCalculationMap } from '../../utils/build-group-total-calculation-map.util';

import { ServiceUsageCardCmp_ResolvableServiceUsage_Fragment } from '../../components/ServiceUsages/Card/fragments/resolvable-service-usage.fragment';

import { ResolvableServiceUsageForm_ResolvableServiceUsage_Fragment } from '../ResolvableServiceUsage/fragments/resolvable-service-usage.fragment';
import { ResolvableServiceUsagesGroupForm_ResolvableServiceUsagesGroup_Fragment } from '../ResolvableServiceUsage/fragments/resolvable-service-usages-group.fragment';

import { ResolvableServiceUsagesGroupFormStep } from './Group';
import { MoveToGroupForm } from './MoveToGroup';
import { ReorderDrawer } from './ReorderDrawer';
import { SwitchGroupingModeForm } from './SwitchGroupingMode';
import { useFormGroupMutations } from './hooks/useFormGroupMutations.hook';
import { useMoveToGroupSubmit } from './hooks/useMoveToGroupSubmit.hook';
import { useDirectMutationCallbacks } from './hooks/useDirectMutationCallbacks.hook';
import { useResolvableServiceUsageLabel } from './hooks/useResolvableServiceUsageLabel.hook';
import { useServiceUsagesGroupActions } from './hooks/useServiceUsagesGroupActions.hook';
import { useSwitchGroupingModeSubmit } from './hooks/useSwitchGroupingModeSubmit.hook';
import { memoForm2ApiMapper } from '../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import type { ReorderDrawerValues } from './ReorderDrawer';
import type { Props } from './types/props.type';
import type { ResolvableServiceUsagesGroupsContext } from './types/groups-context.type';
import type { SubmitHandler } from './types/submit-handler.type';

type ContextualizedFields = Readonly<
  Pick<ResolvableServiceUsagesGroupsContext, 'serviceUsagesGroups'>
>;

const noMutation = () => undefined;

interface ServiceUsageToMove {
  readonly serviceUsageId: Id;
  readonly sourceServiceUsagesGroupId: Id;
}

export const ResolvableServiceUserFormStep = (props: Readonly<Props>) => {
  const {
    fallbackResolvableServiceUsagesGroups,
    formContext,
    leadingContent,
    ...groupProps
  } = props;
  const hasLeadingContent = leadingContent !== undefined;
  const {
    additionalTotalGroups = EMPTY_ARRAY,
    groupTotalCalculations,
    mutations,
    paths,
    queryKeysToInvalidate,
    selectedOptionGroupIds,
    optionGroupsSelection,
    totalCalculation: globalTotalCalculation,
    totalSections,
    useDirectMutation,
  } = props;

  const groupTotalCalculationMap = useMemo(
    () => buildGroupTotalCalculationMap(groupTotalCalculations),
    [groupTotalCalculations],
  );

  const buildGlobalTotalOptions = (
    groups: readonly GlobalTotalOptionsGroup[],
  ): ServiceUsagesGlobalTotalOptions => ({
    groupTotalCalculationMap,
    groups: [...additionalTotalGroups, ...groups],
    initialSelectedOptionGroupIds: selectedOptionGroupIds,
    sections: totalSections,
    selectionControl: optionGroupsSelection,
  });

  const { values } = useFormikContext<Readonly<{ _id: Id }>>();

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();

  const [targetGroupingMode, setTargetGroupingMode] =
    useState<GroupingModeEnum | null>(null);

  const closeSwitchDialog = useCallback(() => {
    setTargetGroupingMode(null);
  }, []);

  const handleStartGrouping = useCallback(() => {
    setTargetGroupingMode(GroupingModeEnum.Manual);
  }, []);

  const handleStartUngroup = useCallback(() => {
    setTargetGroupingMode(GroupingModeEnum.Singleton);
  }, []);

  const [serviceUsageToMove, setServiceUsageToMove] =
    useState<ServiceUsageToMove | null>(null);

  const closeMoveDialog = useCallback(() => {
    setServiceUsageToMove(null);
  }, []);

  const fields = useContextualizedFields<ContextualizedFields>(
    {
      serviceUsagesGroups: {
        id: 'service-usages-groups',
        name: 'serviceUsagesGroups',
      },
    },
    formContext,
  );

  const ownGroups = getFragmentData(
    ResolvableServiceUsagesGroupForm_ResolvableServiceUsagesGroup_Fragment,
    fields.serviceUsagesGroups.value,
  );

  const groupFormContext = (position: number) =>
    formContext
      ? `${formContext}.serviceUsagesGroups.${position.toString()}`
      : `serviceUsagesGroups.${position.toString()}`;

  // Formik paths address groups by their position in the stored array, which
  // may differ from the display order given by `index`.
  const displayedGroups = sortBy(
    (ownGroups ?? fallbackResolvableServiceUsagesGroups ?? []).map(
      (group, position) => ({ group, position }),
    ),
    ({ group }) => group.index,
  );

  const formGroupMutations = useFormGroupMutations({
    groups: ownGroups,
    name: fields.serviceUsagesGroups.name,
  });

  const groupingMutations = useDirectMutation ? mutations : formGroupMutations;

  const switchGroupingModeMutation = groupingMutations.switchGroupingMode;

  const canSwitchGroupingMode = !!switchGroupingModeMutation && !!ownGroups;

  const { handleSubmitFactory } = useSwitchGroupingModeSubmit({
    mutation: switchGroupingModeMutation ?? noMutation,
    onSuccess: closeSwitchDialog,
    queryKeysToInvalidate,
    serviceUserId: values._id,
  });

  const moveToGroupMutation = groupingMutations.moveToGroup;

  const canMoveToGroup = !!moveToGroupMutation && (ownGroups?.length ?? 0) > 1;

  const { handleSubmitFactory: handleMoveSubmitFactory } = useMoveToGroupSubmit(
    {
      mutation: moveToGroupMutation ?? noMutation,
      onSuccess: closeMoveDialog,
      queryKeysToInvalidate,
      serviceUserId: values._id,
    },
  );

  const moveToGroupDialog =
    canMoveToGroup && !!serviceUsageToMove
      ? createPortal(
          <MoveToGroupForm
            askMemoBeforeSubmit={useDirectMutation}
            isDialogOpen
            onCloseDialog={closeMoveDialog}
            onSubmit={handleMoveSubmitFactory(
              serviceUsageToMove.serviceUsageId,
            )}
            targetGroups={displayedGroups
              .map(({ group }) => group)
              .filter(
                (group) =>
                  group._id !== serviceUsageToMove.sourceServiceUsagesGroupId,
              )}
          />,
          document.body,
        )
      : null;

  const groupMutations = groupingMutations.groups;

  const canManageGroups = !!groupMutations && !!ownGroups;

  const {
    dialog: groupActionDialog,
    handleStartCreateGroup,
    handleStartRemoveGroup,
    handleStartRenameGroup,
  } = useServiceUsagesGroupActions({
    askMemoBeforeSubmit: useDirectMutation,
    groups: displayedGroups.map(({ group }) => group),
    mutations: groupMutations,
    queryKeysToInvalidate,
    serviceUserId: values._id,
  });

  const [isReorderDrawerOpenLocally, setIsReorderDrawerOpenLocally] =
    useState(false);

  const isReorderDrawerOpen = paths
    ? currentPath === paths.editReorder
    : isReorderDrawerOpenLocally;

  const handleStartReorder = useCallback(() => {
    paths
      ? void navigate(paths.editReorder)
      : setIsReorderDrawerOpenLocally(true);
  }, [navigate, paths]);

  const handleReorderDrawerClose = useCallback(() => {
    paths ? void navigate(paths.edit) : setIsReorderDrawerOpenLocally(false);
  }, [navigate, paths]);

  const { getLabel: getServiceUsageLabel } = useResolvableServiceUsageLabel({
    fallbackResolvableServiceUsages: fallbackResolvableServiceUsagesGroups
      ? getFragmentData(
          ServiceUsageCardCmp_ResolvableServiceUsage_Fragment,
          fallbackResolvableServiceUsagesGroups.flatMap(
            (fallbackGroup) => fallbackGroup.serviceUsages ?? [],
          ),
        )
      : undefined,
  });

  const reorderDrawerGroups = useMemo(
    () =>
      sortBy(ownGroups ?? [], 'index').map((group) => ({
        groupId: group._id,
        label:
          group.groupingMode === GroupingModeEnum.Singleton
            ? SINGLETON_GROUP_LABEL
            : (group.label ?? EMPTY_STRING),
        serviceUsages: getFragmentData(
          ResolvableServiceUsageForm_ResolvableServiceUsage_Fragment,
          group.serviceUsages,
        ).map((serviceUsage) => ({
          _id: serviceUsage._id,
          label: getServiceUsageLabel(serviceUsage),
        })),
      })),
    [ownGroups, getServiceUsageLabel],
  );

  const reorderGroupsMutation = groupMutations?.reorderGroups;

  const canReorder = !!reorderGroupsMutation && !!ownGroups;

  const reorderServiceUserId = values._id;

  const buildReorderCallbacks = useDirectMutationCallbacks({
    onSuccess: handleReorderDrawerClose,
    queryKeysToInvalidate,
  });

  const handleReorderSubmit = useCallback<SubmitHandler<ReorderDrawerValues>>(
    ({
      values: reorderDrawerValues,
      memos,
      formikHelpers: { setSubmitting },
      setFormError,
    }) => {
      setFormError(null);

      reorderGroupsMutation?.(
        {
          groups: reorderDrawerValues.groups,
          serviceUserId: reorderServiceUserId,
          versionData: memoForm2ApiMapper(memos),
        },
        buildReorderCallbacks({
          setFormError,
          setSubmitting,
          successMessage: 'Les prestations ont été réordonnées.',
        }),
      );

      return undefined;
    },
    [buildReorderCallbacks, reorderGroupsMutation, reorderServiceUserId],
  );

  const switchGroupingModeDialog =
    canSwitchGroupingMode && !!targetGroupingMode
      ? createPortal(
          <SwitchGroupingModeForm
            askMemoBeforeSubmit={useDirectMutation}
            isDialogOpen
            onCloseDialog={closeSwitchDialog}
            onSubmit={handleSubmitFactory(targetGroupingMode)}
            targetGroupingMode={targetGroupingMode}
          />,
          document.body,
        )
      : null;

  const totalGroupsCount =
    additionalTotalGroups.length + displayedGroups.length;
  const hasGlobalTotal =
    !!globalTotalCalculation &&
    shouldShowGlobalTotal(totalGroupsCount, globalTotalCalculation);

  if (
    isInManualGroupingMode(
      ownGroups ?? fallbackResolvableServiceUsagesGroups ?? undefined,
    )
  ) {
    const globalTotal = hasGlobalTotal ? (
      <ServiceUsagesGlobalTotal
        onStartCreateGroup={
          canManageGroups ? handleStartCreateGroup : undefined
        }
        onStartReorder={canReorder ? handleStartReorder : undefined}
        onStartSwitchGroupingMode={
          canSwitchGroupingMode ? handleStartUngroup : undefined
        }
        options={buildGlobalTotalOptions(
          displayedGroups.map(({ group }) => group),
        )}
        totalCalculation={globalTotalCalculation}
      />
    ) : undefined;

    return (
      <>
        <Box sx={{ width: '100%' }}>
          {hasLeadingContent ? (
            <>
              <Box sx={{ pt: 2 }}>{globalTotal}</Box>
              {leadingContent}
            </>
          ) : null}
          {displayedGroups.map(({ group, position }, displayPosition) => (
            <Box component="section" key={group._id} sx={{ mb: 3, pt: 2 }}>
              <ResolvableServiceUsagesGroupFormStep
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...groupProps}
                fallbackResolvableServiceUsages={
                  ownGroups
                    ? fallbackResolvableServiceUsagesGroups?.flatMap(
                        (fallbackGroup) => fallbackGroup.serviceUsages ?? [],
                      )
                    : fallbackResolvableServiceUsagesGroups?.[position]
                        ?.serviceUsages
                }
                formContext={groupFormContext(position)}
                globalTotal={
                  displayPosition === 0 && !hasLeadingContent
                    ? globalTotal
                    : undefined
                }
                globalTotalErrorMessages={
                  displayedGroups.length === 1
                    ? getTotalCalculationErrorMessages(globalTotalCalculation)
                    : undefined
                }
                groupIsOptional={group.isOptional}
                groupLabel={group.label}
                groupingMode={group.groupingMode}
                isInGroup
                manualTotal={group.manualTotal}
                maskAmounts={group.totalMode === TotalModeEnum.Manual}
                onStartCreateGroup={
                  canManageGroups && displayedGroups.length === 1
                    ? handleStartCreateGroup
                    : undefined
                }
                // eslint-disable-next-line react/jsx-no-bind
                onStartMove={
                  canMoveToGroup
                    ? (serviceUsageId) => {
                        setServiceUsageToMove({
                          serviceUsageId,
                          sourceServiceUsagesGroupId: group._id,
                        });
                      }
                    : undefined
                }
                // eslint-disable-next-line react/jsx-no-bind
                onStartRemoveGroup={
                  canManageGroups &&
                  displayedGroups.length > 1 &&
                  !group.serviceUsages?.length
                    ? () => {
                        handleStartRemoveGroup(group);
                      }
                    : undefined
                }
                // eslint-disable-next-line react/jsx-no-bind
                onStartRenameGroup={
                  canManageGroups
                    ? () => {
                        handleStartRenameGroup(group);
                      }
                    : undefined
                }
                onStartReorder={canReorder ? handleStartReorder : undefined}
                onStartSwitchGroupingMode={
                  canSwitchGroupingMode && displayedGroups.length === 1
                    ? handleStartUngroup
                    : undefined
                }
                serviceUsagesGroupId={group._id}
                showInheritanceAlert={displayPosition === 0}
                showTotalCalculation={groupTotalCalculationMap.has(group._id)}
                totalCalculation={groupTotalCalculationMap.get(group._id)}
              />
            </Box>
          ))}
        </Box>
        {switchGroupingModeDialog}
        {moveToGroupDialog}
        {groupActionDialog}
        <ReorderDrawer
          askMemoBeforeSubmit={useDirectMutation}
          groups={reorderDrawerGroups}
          isOpen={isReorderDrawerOpen}
          onDrawerClose={handleReorderDrawerClose}
          onSubmit={handleReorderSubmit}
        />
      </>
    );
  }

  const [singletonDisplay] = displayedGroups;
  const [singletonFallbackGroup] = sortBy(
    fallbackResolvableServiceUsagesGroups ?? [],
    'index',
  );

  invariant(
    !!singletonDisplay,
    'Expected exactly one service usages group in Singleton mode',
  );

  const singletonUsagesCount =
    reorderDrawerGroups[0]?.serviceUsages.length ?? 0;

  const canReorderSingleton = canReorder && singletonUsagesCount > 1;

  return (
    <>
      {hasGlobalTotal ? (
        <Box sx={{ pt: 2 }}>
          <ServiceUsagesGlobalTotal
            options={buildGlobalTotalOptions([singletonDisplay.group])}
            totalCalculation={globalTotalCalculation}
          />
        </Box>
      ) : null}
      {leadingContent}
      <ResolvableServiceUsagesGroupFormStep
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...groupProps}
        fallbackResolvableServiceUsages={singletonFallbackGroup?.serviceUsages}
        formContext={groupFormContext(
          ownGroups ? singletonDisplay.position : 0,
        )}
        globalTotalErrorMessages={getTotalCalculationErrorMessages(
          globalTotalCalculation,
        )}
        groupingMode={singletonDisplay.group.groupingMode}
        manualTotal={singletonDisplay.group.manualTotal}
        maskAmounts={singletonDisplay.group.totalMode === TotalModeEnum.Manual}
        onStartReorder={canReorderSingleton ? handleStartReorder : undefined}
        onStartSwitchGroupingMode={
          canSwitchGroupingMode ? handleStartGrouping : undefined
        }
        serviceUsagesGroupId={singletonDisplay.group._id}
        totalCalculation={
          groupTotalCalculationMap.get(singletonDisplay.group._id) ??
          globalTotalCalculation
        }
      />
      {switchGroupingModeDialog}
      <ReorderDrawer
        askMemoBeforeSubmit={useDirectMutation}
        groups={reorderDrawerGroups}
        isOpen={isReorderDrawerOpen}
        onDrawerClose={handleReorderDrawerClose}
        onSubmit={handleReorderSubmit}
      />
    </>
  );
};
